import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { GuestSummary, GuestsResponse } from '@mv/contracts';
import { IconBack } from '../components/Icon';
import { SUIT_COLOR, SUIT_MARK, useSession } from '../lib/session';
import { api } from '../lib/api';
import { fmtDay, fmtTime, now, relativeUploadTime } from '../lib/time';

/**
 * The organiser's view of who is here.
 *
 * Read-only on purpose. D37 says no admin console, and this does not become
 * one: it answers "who has signed in, on which team, and have they uploaded
 * anything" and offers no button that changes any of it. Promoting someone to
 * organiser stays a deliberate act from a terminal rather than something an
 * unlocked phone on a table can do.
 */
export default function AdminGuests() {
  const session = useSession();
  const navigate = useNavigate();
  const [data, setData] = useState<GuestsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api<GuestsResponse>('/admin/guests')
      .then((d) => { if (live) setData(d); })
      .catch(() => { if (live) setError('That did not load. Try again in a moment.'); });
    return () => { live = false; };
  }, []);

  // The API enforces this; the client only avoids rendering a screen that would
  // 403 anyway. Both checks read the same `admin` group claim.
  if (session && !session.isAdmin) {
    return (
      <div className="page page-pad empty" style={{ minHeight: '60dvh', justifyContent: 'center' }}>
        <div className="mark" aria-hidden="true">♠</div>
        <h4 style={{ margin: 0 }}>Not this one</h4>
        <p>That page is for organisers.</p>
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/me')}>Back to your profile</button>
      </div>
    );
  }

  return (
    <div className="page page-narrow page-narrow-lg" style={{ padding: '10px 20px 96px' }}>
      <button type="button" className="btn btn-secondary" style={{ minHeight: 42, gap: 8 }} onClick={() => navigate('/me')}>
        <IconBack size={16} /> Profile
      </button>

      <h3 style={{ margin: '16px 0 4px' }}>Guests</h3>
      {data && (
        <p style={{ fontSize: 13, opacity: 0.7, margin: 0 }}>
          {data.guests.length} signed in · {data.totalUploads} {data.totalUploads === 1 ? 'upload' : 'uploads'} between them
        </p>
      )}

      {error && <p style={{ fontSize: 13, color: 'var(--color-accent-700)', marginTop: 16 }} role="alert">{error}</p>}

      {!data && !error && (
        <div className="stack" style={{ gap: 8, marginTop: 18 }}>
          {Array.from({ length: 6 }, (_, i) => <div key={i} className="sk sk-block" style={{ height: 62 }} />)}
        </div>
      )}

      {data && data.guests.length === 0 && (
        <div className="empty" style={{ marginTop: 24 }}>
          <div className="mark" aria-hidden="true">♦</div>
          <h4 style={{ margin: 0 }}>Nobody yet</h4>
          <p>The first guest to sign in appears here.</p>
        </div>
      )}

      {data && data.guests.length > 0 && (
        <div className="stack" style={{ gap: 8, marginTop: 18 }}>
          {data.guests.map((g) => <GuestRow key={g.userId} guest={g} />)}
        </div>
      )}

      {data && data.guests.length > 0 && (
        <p style={{ fontSize: 11, opacity: 0.5, marginTop: 18 }}>
          {/* Saying "last seen" rather than "last login" is not pedantry: the
              timestamp updates on silent token refreshes too, so it tracks use
              rather than sign-in. Calling it a login would be a lie an
              organiser might act on. */}
          "Last seen" is the last time someone had the app open, not the last time they signed in.
          Sorted by it, most recent first. Times are Khao Lak time.
        </p>
      )}
    </div>
  );
}

function GuestRow({ guest }: { guest: GuestSummary }) {
  const seen = guest.lastSeen ?? guest.registeredAt;
  const fresh = now() - seen < 36 * 3600_000;

  return (
    <div className="card guest-row">
      <span className="suit" style={{ color: SUIT_COLOR[guest.team], fontSize: 20 }} title={guest.team}>
        {SUIT_MARK[guest.team]}
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="guest-name">
          {guest.displayName}
          {guest.isAdmin && <span className="tag tag-accent-2" style={{ fontSize: 10 }}>Organiser</span>}
        </div>
        <div className="guest-sub">{guest.email}</div>
      </div>

      <div className="guest-meta">
        <div className="guest-uploads">
          {guest.uploads}
          <span>{guest.uploads === 1 ? 'photo' : 'photos'}</span>
        </div>
        {/* The tooltip names the timezone because the bare time does not, and
            an organiser reading this from another continent before the trip
            has no way to tell that 06:01 is Khao Lak's 06:01 and not theirs.
            Everything on the site renders in Asia/Bangkok on purpose — see
            `time.ts` — and during the week that is simply the local clock. */}
        <div className="guest-seen" title={`${fmtDay(seen)} ${fmtTime(seen)} Khao Lak time`}>
          {/* Once the week is under way, "3 min ago" is what an organiser wants.
              Before it, a relative time reads as noise, so it falls back to a
              date. */}
          {fresh ? relativeUploadTime(seen) : fmtDay(seen)}
        </div>
      </div>
    </div>
  );
}
